import { Head, Link, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft, Banknote, CheckCircle2, RotateCcw } from 'lucide-react'

interface Payment {
  id: number
  kind: 'agent_shortfall' | 'sponsor_shortfall'
  status: 'pending' | 'reviewed' | 'paid'
  amount: number
  agent: { id: number; name: string } | null
  carrier: { id: number; name: string; code: string } | null
  commission: {
    id: number
    policy_number: string | null
    gross_amount: number
    received_amount: number
    agent_amount: number
    sponsor_amount: number
    carrier_payout_percentage: number
  } | null
  created_at: string
  reviewed_at: string | null
  paid_at: string | null
}

interface Props extends PageProps {
  payment: Payment
}

const fmtMoney = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)

export default function PaymentShow({ payment }: Props) {
  const setStatus = (status: 'pending' | 'reviewed' | 'paid') => {
    router.patch(`/admin/payments/${payment.id}`, { status })
  }

  const c = payment.commission
  const shortfall = c ? c.gross_amount - c.received_amount : 0

  return (
    <AppLayout>
      <Head title={`Payment #${payment.id}`} />

      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link href="/admin/payments" className="p-2 text-charcoal/60 hover:text-forest-green">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold font-heading text-chestnut-charcoal flex items-center gap-2">
            <Banknote className="w-6 h-6 text-forest-green" />
            Payment #{payment.id}
          </h1>
          <span className={`px-2 py-0.5 text-xs rounded capitalize ${
            payment.status === 'paid' ? 'bg-forest-green/15 text-forest-green'
            : payment.status === 'reviewed' ? 'bg-gold/30 text-charcoal'
            : 'bg-burgundy/10 text-burgundy'
          }`}>{payment.status}</span>
        </div>

        <div className="bg-white border border-charcoal/10 rounded-lg p-5 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <Field label="Agent" value={payment.agent?.name || '—'} />
          <Field label="Carrier" value={payment.carrier ? `${payment.carrier.name} (${payment.carrier.code})` : '—'} />
          <Field label="Kind" value={payment.kind === 'agent_shortfall' ? 'Writing agent shortfall' : 'Sponsor shortfall'} />
          <Field label="Amount" value={<span className="font-mono">{fmtMoney(payment.amount)}</span>} />
          <Field label="Created" value={new Date(payment.created_at).toLocaleString()} />
          <Field label="Reviewed" value={payment.reviewed_at ? new Date(payment.reviewed_at).toLocaleString() : '—'} />
          <Field label="Paid" value={payment.paid_at ? new Date(payment.paid_at).toLocaleString() : '—'} />
        </div>

        {c && (
          <div className="bg-white border border-charcoal/10 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-serif text-forest-green">Source Commission</h2>
              <Link href={`/commissions/${c.id}`} className="text-sm text-forest-green hover:underline">
                Open commission #{c.id} →
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field label="Policy #" value={c.policy_number || '—'} />
              <Field label="Carrier Payout %" value={`${c.carrier_payout_percentage}%`} />
              <Field label="Expected (100%)" value={<span className="font-mono">{fmtMoney(c.gross_amount)}</span>} />
              <Field label="Received" value={<span className="font-mono">{fmtMoney(c.received_amount)}</span>} />
              <Field label="Expected Agent" value={<span className="font-mono">{fmtMoney(c.agent_amount)}</span>} />
              <Field label="Expected Sponsor" value={<span className="font-mono">{fmtMoney(c.sponsor_amount)}</span>} />
              <Field label="Total Shortfall" value={<span className="font-mono text-burgundy">{fmtMoney(shortfall)}</span>} />
            </div>
          </div>
        )}

        <div className="bg-white border border-charcoal/10 rounded-lg p-5">
          <h2 className="text-lg font-serif text-forest-green mb-3">Status</h2>
          <div className="flex gap-2 flex-wrap">
            {payment.status !== 'reviewed' && payment.status !== 'paid' && (
              <button
                onClick={() => setStatus('reviewed')}
                className="px-4 py-2 text-sm bg-gold/30 border border-gold text-charcoal rounded hover:bg-gold/40 flex items-center gap-1"
              >
                <CheckCircle2 className="w-4 h-4" /> Mark Reviewed
              </button>
            )}
            {payment.status !== 'paid' && (
              <button
                onClick={() => setStatus('paid')}
                className="px-4 py-2 text-sm bg-forest-green text-cream rounded hover:bg-forest-green/90 flex items-center gap-1"
              >
                <CheckCircle2 className="w-4 h-4" /> Mark Paid
              </button>
            )}
            {payment.status !== 'pending' && (
              <button
                onClick={() => setStatus('pending')}
                className="px-4 py-2 text-sm border border-charcoal/20 text-charcoal rounded hover:bg-charcoal/5 flex items-center gap-1"
              >
                <RotateCcw className="w-4 h-4" /> Revert to Pending
              </button>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-charcoal/60">{label}</div>
      <div className="text-charcoal mt-0.5">{value}</div>
    </div>
  )
}
